<script lang="ts" setup>
import { defineComponent, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { Constants } from '@/utilities/constants.ts';

const { t } = useI18n();

const social = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/ProjetHabitation',
    icon: defineComponent({
      render: () =>
        h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
          h('path', {
            'fill-rule': 'evenodd',
            d: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z',
            'clip-rule': 'evenodd',
          }),
        ]),
    }),
  },
  {
    name: 'X',
    href: 'https://x.com/PHabitation',
    icon: defineComponent({
      render: () =>
        h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
          h('path', {
            d: 'M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z',
          }),
        ]),
    }),
  },
];

const navigation = {
  projects: [
    { name: t('footer.categories.projects.houses-for-sale.title'), route: { name: 'editorial-category', params: { categorySlug: t('footer.categories.projects.houses-for-sale.category-slug') } } },
    { name: t('footer.categories.projects.condos-for-sale.title'), route: { name: 'editorial-category', params: { categorySlug: t('footer.categories.projects.condos-for-sale.category-slug') } } },
    { name: t('footer.categories.projects.condos-for-rent.title'), route: { name: 'editorial-category', params: { categorySlug: t('footer.categories.projects.condos-for-rent.category-slug') } } },
  ],
  help: [
    { name: t('footer.categories.help.support.title'), route: { name: 'support' } },
    { name: t('footer.categories.help.advertise.title'), route: { name: 'advertise' } },
  ],
  company: [
    { name: t('footer.categories.company.about.title'), route: { name: 'about' } },
    { name: t('footer.categories.company.directory.title'), route: { name: 'directory' } },
    { name: t('footer.categories.company.builders.title'), route: { name: 'builders' } },
    { name: t('footer.categories.company.editorial.title'), route: { name: 'editorial' } },
    { name: t('footer.categories.company.guides.title'), route: { name: 'guides' } },
  ],
  legal: [
    { name: t('footer.categories.legal.privacy.title'), route: { name: 'privacy' } },
    { name: t('footer.categories.legal.terms.title'), route: { name: 'terms' } },
  ],
};
</script>

<template>
  <footer class="no-print bg-white">
    <div class="mx-auto mt-16 mb-8 max-w-7xl px-6 sm:mt-24 lg:mt-32 lg:px-8">
      <div class="xl:grid xl:grid-cols-3 xl:gap-8">
        <div class="space-y-8">
          <img :alt="t('global.site-name')" :src="`${Constants.CdnBaseUrl}/public/icon.svg`" class="h-7 w-7" fetchpriority="high" loading="lazy" />
          <p class="text-sm leading-6 text-gray-600">{{ t('footer.tag-line') }}</p>
          <div class="flex space-x-6">
            <a v-for="item in social" :key="item.name" :href="item.href" class="text-gray-400 hover:text-gray-500" rel="noopener noreferrer" target="_blank">
              <span class="sr-only">{{ item.name }}</span>
              <component :is="item.icon" aria-hidden="true" class="h-6 w-6" />
            </a>
          </div>
        </div>
        <div class="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
          <div class="md:grid md:grid-cols-2 md:gap-8">
            <div>
              <h3 class="text-sm leading-6 font-semibold text-gray-900">{{ t('footer.categories.projects.title') }}</h3>
              <ul class="mt-6 space-y-4" role="list">
                <li v-for="item in navigation.projects" :key="item.name">
                  <RouterLink :to="item.route" class="text-sm leading-6 text-gray-600 hover:text-gray-900">{{ item.name }}</RouterLink>
                </li>
              </ul>
            </div>
            <div class="mt-10 md:mt-0">
              <h3 class="text-sm leading-6 font-semibold text-gray-900">{{ t('footer.categories.help.title') }}</h3>
              <ul class="mt-6 space-y-4" role="list">
                <li v-for="item in navigation.help" :key="item.name">
                  <RouterLink :to="item.route" class="text-sm leading-6 text-gray-600 hover:text-gray-900">{{ item.name }}</RouterLink>
                </li>
              </ul>
            </div>
          </div>
          <div class="md:grid md:grid-cols-2 md:gap-8">
            <div>
              <h3 class="text-sm leading-6 font-semibold text-gray-900">{{ t('footer.categories.company.title') }}</h3>
              <ul class="mt-6 space-y-4" role="list">
                <li v-for="item in navigation.company" :key="item.name">
                  <RouterLink :to="item.route" class="text-sm leading-6 text-gray-600 hover:text-gray-900">{{ item.name }}</RouterLink>
                </li>
              </ul>
            </div>
            <div class="mt-10 md:mt-0">
              <h3 class="text-sm leading-6 font-semibold text-gray-900">{{ t('footer.categories.legal.title') }}</h3>
              <ul class="mt-6 space-y-4" role="list">
                <li v-for="item in navigation.legal" :key="item.name">
                  <RouterLink :to="item.route" class="text-sm leading-6 text-gray-600 hover:text-gray-900">{{ item.name }}</RouterLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 lg:mt-24">
        <p class="text-xs leading-5 text-gray-500">&copy; {{ new Date().getFullYear() }} {{ t('global.site-name') }}, {{ t('footer.copyright') }}</p>
      </div>
    </div>
  </footer>
</template>

<style lang="css" scoped></style>
